import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { BookingProvider } from "@/context/BookingContext";

import Home from "@/pages/Home";
import SeoPage from "@/pages/SeoPage";
import NotFound from "@/pages/NotFound";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import AppointmentModal from "@/components/AppointmentModal";

// Physician + MedicalClinic schema now live statically in public/index.html.
// Runtime pages inject their own JSON-LD; drop prerendered per-page copies to avoid duplicates.
const useSeoSchema = () => {
  useEffect(() => {
    document.querySelectorAll("script[data-prerendered]").forEach((s) => s.remove());
  }, []);
};

// Lenis smooth scroll + smart anchor navigation across routes
const isExternalHref = (raw) => {
  try {
    const url = new URL(raw, window.location.origin);
    return url.origin !== window.location.origin;
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.debug("Anchor href could not be parsed, treating as internal:", raw, err);
    }
    return false;
  }
};

const handleAnchorClick = (e, lenis, navigate) => {
  const a = e.target.closest("a");
  if (!a) return;
  const raw = a.getAttribute("href") || "";
  if (!raw.includes("#")) return;
  if (a.target === "_blank") return;
  if (isExternalHref(raw)) return;

  const [pathPart, hashPart] = raw.split("#");
  if (!hashPart) return;
  const targetPath = pathPart || window.location.pathname;

  if (targetPath === window.location.pathname) {
    const el = document.querySelector("#" + hashPart);
    if (el) {
      e.preventDefault();
      lenis.scrollTo(el, { offset: -72 });
    }
  } else {
    e.preventDefault();
    navigate(targetPath + "#" + hashPart);
  }
};

const useLenisAnchors = (navigate) => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => handleAnchorClick(e, lenis, navigate);
    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, [navigate]);
};

// On route change: scroll to hash target once the new page's DOM renders
const useHashScroll = (location) => {
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let attempts = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      attempts += 1;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        clearInterval(timer);
      } else if (attempts >= 10) {
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, [location.pathname, location.hash]);
};

const RouterShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  useLenisAnchors(navigate);
  useHashScroll(location);
  return children;
};

function App() {
  useSeoSchema();

  return (
    <BrowserRouter>
      <BookingProvider>
        <RouterShell>
          <div className="App bg-white min-h-screen text-[#0b2c4d] antialiased">
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/:slug" element={<SeoPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
            <FloatingActions />
            <AppointmentModal />
          </div>
          <Toaster position="top-center" theme="light" richColors />
        </RouterShell>
      </BookingProvider>
    </BrowserRouter>
  );
}

export default App;
